/**
 * Axios singleton para tax-admin-frontend.
 * - Interceptor de auth: agrega Authorization: Bearer <accessToken>.
 * - Interceptor de refresh: si 401, intenta refresh token y retry.
 *
 * Fase 1: skeleton mínimo. Fase 7 implementa refresh real contra
 * tax-account.
 */

import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { APP } from './constants'

export const api = axios.create({
  baseURL: APP.apiUrl,
  timeout: 30_000,
  withCredentials: true,
})

// ─── Token storage en memoria (no localStorage por XSS) ───────────
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/**
 * True iff the in-memory access token has a valid JWT shape AND its
 * `exp` claim is in the future. We do NOT verify the RS256 signature
 * client-side — the server will reject tampered tokens via JWKS on
 * the next API call. This guard exists to keep the user out of the
 * dashboard when there's no token, when it's malformed, or when it's
 * expired (so we redirect to /login instead of showing a half-broken
 * UI before the first 401).
 */
export function hasValidAccessToken(): boolean {
  const token = accessToken
  if (!token) return false
  const parts = token.split('.')
  if (parts.length !== 3) return false
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload?.exp !== 'number') return false
    const now = Math.floor(Date.now() / 1000)
    return payload.exp > now
  } catch {
    return false
  }
}

// ─── Request interceptor ───────────────────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// ─── Silent refresh — shared between `api` and `accountApi` ────────
//
// Goal: when an access token expires (401), don't bounce the user to
// /login. Instead, call /auth/refresh (the httpOnly cookie travels
// with the request automatically), get a new access token, retry the
// original request transparently. Multiple requests racing the same
// 401 share a single refresh promise so we don't trigger N rotations.
//
// Two functions exposed: `attemptSilentRefresh()` returns true if the
// new token is in memory and the caller should retry; false if the
// session is dead and the caller should fall back to /login.

let refreshInFlight: Promise<boolean> | null = null

interface RefreshOk {
  access_token: string
  refresh_token?: string
  expires_in?: number
}

export async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      // Use a bare axios call (NOT one of our instances) to avoid
      // recursing through this same interceptor.
      const r = await axios.post<RefreshOk>(
        `${APP.accountUrl}/api/v1/auth/refresh`,
        {},
        { withCredentials: true, timeout: 10_000 },
      )
      if (!r.data?.access_token) {
        setAccessToken(null)
        return false
      }
      setAccessToken(r.data.access_token)
      return true
    } catch {
      setAccessToken(null)
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

function isAuthEndpointPath(url?: string): boolean {
  if (!url) return false
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/register/') ||
    url.includes('/auth/oauth/')
  )
}

function makeRefreshInterceptor(client: typeof api) {
  return async (error: AxiosError) => {
    if (error.response?.status === 403) {
      // Autorización insuficiente (rol admin faltante o :client_id que no
      // coincide con la org activa — guard RequireClientMatchesOrg del
      // backend). NO se redirige ni se intenta refresh (el token es válido;
      // redirigir a /login causaría loops). Toast + reject para que la page
      // muestre su propio estado de error.
      const data = error.response.data as { message?: string; detail?: string } | undefined
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.detail === 'string' && data.detail) ||
        'No tienes acceso a este recurso (organización activa no coincide)'
      toast.error(msg)
      return Promise.reject(error)
    }
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined
    if (
      !original ||
      original._retry ||
      error.response?.status !== 401 ||
      isAuthEndpointPath(original.url)
    ) {
      // Don't refresh on login itself, or if we already retried once,
      // or for non-401 errors.
      if (error.response?.status === 401 && !isAuthEndpointPath(original?.url)) {
        const path = window.location.pathname
        const isAuthPage =
          path === '/login' || path === '/register' || path.startsWith('/oauth/')
        if (!isAuthPage) {
          setAccessToken(null)
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }

    original._retry = true
    const ok = await attemptSilentRefresh()
    if (!ok) {
      const path = window.location.pathname
      const isAuthPage =
        path === '/login' || path === '/register' || path.startsWith('/oauth/')
      if (!isAuthPage) window.location.href = '/login'
      return Promise.reject(error)
    }
    if (original.headers) {
      original.headers.Authorization = `Bearer ${accessToken}`
    }
    return client(original)
  }
}

api.interceptors.response.use((r) => r, makeRefreshInterceptor(api))

export const accountApi = axios.create({
  baseURL: APP.accountUrl,
  timeout: 30_000,
  withCredentials: true,
})

// Same auth + silent-refresh behavior on accountApi (so /auth/me et al
// can transparently rotate when the access token expires).
accountApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})
accountApi.interceptors.response.use(
  (r) => r,
  makeRefreshInterceptor(accountApi),
)

// ─── Registration flow (mirrors core/account contract) ────────────

export interface RegisterStartRequest {
  email: string
  password: string
  first_name: string
  last_name: string
  phone?: string | null
  phone_country_code?: string | null
  registration_type: 'persona' | 'empresa'
  // empresa fields
  nit?: string | null
  razon_social?: string | null
  email_corporativo?: string | null
  telefono?: string | null
  telefono_country_code?: string | null
  nombre_comercial?: string | null
  organizacion_juridica?: string | null
  entity_type?: string
  rues_verified?: boolean
  tamano_empresa?: string | null
  ciiu_principal?: string | null
  ciiu_secundarios?: string[] | null
  tax_id_dv?: string | null
  departamento?: string | null
  municipio?: string | null
  direccion?: string | null
  // Habeas Data — autorización expresa (Ley 1581).
  accepted_terms_version: string
  accepted_habeas_data_version: string
  accepted_marketing?: boolean
}

export interface RegisterStartResponse {
  registration_token: string
  registration_type: 'persona' | 'empresa'
  email_masked: string
  corporate_email_masked: string | null
  expires_in: number
  message: string
}

export interface RegisterVerifyRequest {
  registration_token: string
  personal_code: string
  corporate_code?: string
}

export interface RegisterVerifyResponse {
  person_id: string
  email: string
  first_name: string
  last_name: string
  verification_level: number
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  organization_id?: string | null
  nit?: string | null
  razon_social?: string | null
  org_is_active?: boolean | null
  rues_verified?: boolean | null
  message: string
}

export interface RegisterVerifyError {
  personal_ok: boolean
  corporate_ok?: boolean | null
  attempts_remaining: number
  message: string
}

export interface RegisterResendResponse {
  message: string
  email_masked: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  person_id: string
  email: string
  first_name: string
  last_name: string
  verification_level: number
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  organization_id?: string | null
  organization_role?: string | null
  razon_social?: string | null
  mfa_required?: boolean
  mfa_challenge_token?: string | null
  mfa_challenge_expires_in?: number | null
}

export const authApi = {
  startRegistration: (body: RegisterStartRequest) =>
    accountApi.post<RegisterStartResponse>('/api/v1/auth/register/start', body).then((r) => r.data),

  verifyRegistration: (body: RegisterVerifyRequest) =>
    accountApi.post<RegisterVerifyResponse>('/api/v1/auth/register/verify', body).then((r) => r.data),

  resendOTP: (registration_token: string, target: 'personal' | 'corporate') =>
    accountApi
      .post<RegisterResendResponse>('/api/v1/auth/register/resend', { registration_token, target })
      .then((r) => r.data),

  login: (body: LoginRequest) =>
    accountApi.post<LoginResponse>('/api/v1/auth/login', body).then((r) => r.data),

  loginTotp: (body: { mfa_challenge_token: string; code: string }) =>
    accountApi.post<LoginResponse>('/api/v1/auth/login/totp', body).then((r) => r.data),

  me: () => accountApi.get<MeResponse>('/api/v1/auth/me').then((r) => r.data),

  logout: () =>
    accountApi
      .post<void>('/api/v1/auth/logout', {})
      .then((r) => r.data)
      .catch(() => undefined), // tolerate 401 when token already revoked

  switchOrg: (organization_id: string) =>
    accountApi
      .post<SwitchOrgResponse>('/api/v1/auth/switch-org', { organization_id })
      .then((r) => r.data),
}

// ─── Identity bootstrap (used by header user menu) ─────────────────

export interface MeActiveOrg {
  id: string
  tax_id: string
  razon_social: string
  nombre_comercial: string | null
  entity_type: string
  role: string | null
}

export interface MembershipOrg {
  id: string
  tax_id: string
  razon_social: string
  nombre_comercial: string | null
  entity_type: string
  is_active: boolean
}

export interface MembershipInfo {
  membership_id: string
  organization_id: string
  role: string
  status: string
  is_active_session_org: boolean
  organization: MembershipOrg | null
}

export interface MeResponse {
  person_id: string
  session_id: string
  email: string
  first_name: string | null
  last_name: string | null
  full_name: string
  initials: string
  avatar_url: string | null
  email_verified: boolean
  verification_level: number
  totp_enabled: boolean
  active_org: MeActiveOrg | null
  memberships_count: number
  memberships: MembershipInfo[]
}

// ─── Switch active organization (JWT con active_org_id distinto) ───

export interface SwitchOrgResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  active_org_id: string
  active_org_role: string
  razon_social: string | null
}

// ─── Public NIT lookup (RUES via empresas.lexplatform.co) ─────────

export interface NITLookupResult {
  found: boolean
  nit: string
  razon_social: string | null
  email_corporativo: string | null
  direccion: string | null
  departamento: string | null
  municipio: string | null
  organizacion_juridica: string | null
  tamano_empresa: string | null
  actividades_economicas: string[]
  rep_legal: string | null
}

export const lookupApi = {
  byNIT: (nit: string) =>
    accountApi.get<NITLookupResult>(`/api/v1/lookup/nit/${encodeURIComponent(nit)}`).then((r) => r.data),
}

// ─── MFA recovery (lost both app + backup codes) ──────────────────

export interface MfaRecoveryRequestResponse {
  ok: boolean
  message: string
  not_before: string
}

export const mfaRecoveryApi = {
  request: (body: { email: string; password: string }) =>
    accountApi
      .post<MfaRecoveryRequestResponse>('/api/v1/auth/mfa/recovery/request', body)
      .then((r) => r.data),
  confirm: (token: string) =>
    accountApi
      .post<{ ok: boolean; message: string }>('/api/v1/auth/mfa/recovery/confirm', { token })
      .then((r) => r.data),
  cancel: (token: string) =>
    accountApi
      .get<{ ok: boolean; message: string }>(
        `/api/v1/auth/mfa/recovery/cancel?token=${encodeURIComponent(token)}`,
      )
      .then((r) => r.data),
}

// ─── Profile / account-settings (mounted at /api/v1/me/*) ─────────

export interface ProfileResponse {
  person_id: string
  email: string
  email_verified: boolean
  phone: string | null
  phone_country_code: string | null
  phone_verified: boolean
  first_name: string
  last_name: string
  full_name: string
  initials: string
  avatar_url: string | null
  country_iso: string
  preferred_locale: string | null
  preferred_timezone: string | null
  verification_level: number
  totp_enabled: boolean
  has_password: boolean
  last_login_at: string | null
  last_login_ip: string | null
  password_changed_at: string | null
  created_at: string
}

export interface UpdateProfileRequest {
  first_name?: string
  last_name?: string
}

export interface UpdatePreferencesRequest {
  preferred_locale?: string | null
  preferred_timezone?: string | null
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface ChangePasswordResponse {
  ok: boolean
  other_sessions_revoked: number
  message: string
}

export interface OAuthAccountInfo {
  provider: 'google' | 'microsoft'
  provider_email: string | null
  linked_at: string
  last_login_at: string | null
}

export interface OAuthAccountsResponse {
  accounts: OAuthAccountInfo[]
}

export interface MfaBackupCodesStatusResponse {
  enabled: boolean
  total: number
  remaining: number
}

export interface MfaBackupCodesRegenerateResponse {
  backup_codes: string[]
  message: string
}

export interface SetPasswordRequest {
  new_password: string
  totp_code?: string | null
}

export interface EmailChangeStartRequest {
  new_email: string
  password?: string | null
  totp_code?: string | null
}

export interface EmailChangeStartResponse {
  ok: boolean
  new_email_masked: string
  expires_in: number
  message: string
}

export interface EmailChangeVerifyResponse {
  ok: boolean
  email: string
  message: string
}

export interface ChangePhoneRequest {
  phone?: string | null
  phone_country_code?: string | null
  password?: string | null
  totp_code?: string | null
}

export interface DeleteAccountRequest {
  password: string
  totp_code?: string | null
  confirmation: string
  reason?: string | null
}

export interface DeleteAccountResponse {
  ok: boolean
  deactivated_at: string
  grace_period_days: number
  message: string
}

export interface SessionInfo {
  id: string
  is_active: boolean
  is_current: boolean
  ip_address: string | null
  user_agent: string | null
  created_at: string | null
  last_activity_at: string | null
  expires_at: string | null
  revoked_at: string | null
  revoked_reason: string | null
}

export interface SessionsResponse {
  current_session_id: string
  sessions: SessionInfo[]
}

export interface LoginHistoryEvent {
  event_type: string
  timestamp: string | null
  ip_address: string | null
  user_agent: string | null
  details: Record<string, unknown> | null
}

export interface LoginHistoryResponse {
  events: LoginHistoryEvent[]
}

export interface TotpSetupResponse {
  secret_b32: string
  otpauth_uri: string
  qr_svg: string
  expires_in: number
}

export interface TotpVerifyResponse {
  backup_codes: string[]
}

export const profileApi = {
  get: () => accountApi.get<ProfileResponse>('/api/v1/me/profile').then((r) => r.data),
  update: (body: UpdateProfileRequest) =>
    accountApi.patch<ProfileResponse>('/api/v1/me/profile', body).then((r) => r.data),
  updatePreferences: (body: UpdatePreferencesRequest) =>
    accountApi.patch<ProfileResponse>('/api/v1/me/preferences', body).then((r) => r.data),
  changePassword: (body: ChangePasswordRequest) =>
    accountApi.post<ChangePasswordResponse>('/api/v1/me/password', body).then((r) => r.data),
  setPassword: (body: SetPasswordRequest) =>
    accountApi.post<ChangePasswordResponse>('/api/v1/me/password/set', body).then((r) => r.data),
  emailChangeStart: (body: EmailChangeStartRequest) =>
    accountApi.post<EmailChangeStartResponse>('/api/v1/me/email/change/start', body).then((r) => r.data),
  emailChangeVerify: (code: string) =>
    accountApi.post<EmailChangeVerifyResponse>('/api/v1/me/email/change/verify', { code }).then((r) => r.data),
  changePhone: (body: ChangePhoneRequest) =>
    accountApi.post<ProfileResponse>('/api/v1/me/phone', body).then((r) => r.data),
  listOAuthAccounts: () =>
    accountApi.get<OAuthAccountsResponse>('/api/v1/me/oauth-accounts').then((r) => r.data),
  disconnectOAuth: (provider: 'google' | 'microsoft') =>
    accountApi.delete<void>(`/api/v1/me/oauth-accounts/${provider}`).then((r) => r.data),
  backupCodesStatus: () =>
    accountApi.get<MfaBackupCodesStatusResponse>('/api/v1/me/mfa/backup-codes/status').then((r) => r.data),
  regenerateBackupCodes: (code: string) =>
    accountApi
      .post<MfaBackupCodesRegenerateResponse>('/api/v1/me/mfa/backup-codes/regenerate', { code })
      .then((r) => r.data),
  deleteAccount: (body: DeleteAccountRequest) =>
    accountApi.post<DeleteAccountResponse>('/api/v1/me/account/delete', body).then((r) => r.data),
  loginHistory: (limit = 20) =>
    accountApi
      .get<LoginHistoryResponse>(`/api/v1/me/login-history?limit=${limit}`)
      .then((r) => r.data),
}

// ─── Sessions ──────────────────────────────────────────────────────

export const sessionsApi = {
  list: () => accountApi.get<SessionsResponse>('/api/v1/auth/sessions').then((r) => r.data),
  revoke: (sessionId: string) =>
    accountApi.delete<void>(`/api/v1/auth/sessions/${sessionId}`).then((r) => r.data),
  revokeAllOthers: () =>
    accountApi.delete<{ revoked: number }>('/api/v1/auth/sessions').then((r) => r.data),
}

// ─── MFA TOTP ──────────────────────────────────────────────────────

export const mfaApi = {
  status: () =>
    accountApi.get<{ enabled: boolean; pending_setup: boolean }>('/api/v1/auth/totp/status').then((r) => r.data),
  setup: () => accountApi.post<TotpSetupResponse>('/api/v1/auth/totp/setup', {}).then((r) => r.data),
  verify: (code: string) =>
    accountApi.post<TotpVerifyResponse>('/api/v1/auth/totp/verify', { code }).then((r) => r.data),
  disable: (password: string, code: string) =>
    accountApi.post<void>('/api/v1/auth/totp/disable', { password, code }).then((r) => r.data),
}

// ─── Ops dashboard (superadmin — vista cross-tenant SCC) ───────────

export interface OpsClientStats {
  total: number
  active: number
  suspended: number
  facturador_final: number
  erp_partner: number
  new_last_7_days: number
}

export interface OpsEmissionStats {
  docs_today: number
  docs_last_7_days: number
  accepted: number
  rejected: number
  failed: number
  in_flight: number
  acceptance_rate_24h: number // -1 = N/A
  transmitted_24h: number
  gross_accepted_7d_cop: string
}

export interface OpsDayEmission {
  day: string // YYYY-MM-DD
  total: number
  accepted: number
  rejected: number
}

export interface OpsDeliveryStats {
  webhook_dlq_depth: number
  webhooks_pending: number
  emails_failed_24h: number
  emails_sent_24h: number
}

export interface OpsSystemHealth {
  storage_provider: string
  dian_configured: boolean
  dian_environment: string
  transmitter_mode: string
  cert_days_to_expiry: number // -1 = sin cert
}

export interface OpsSnapshot {
  generated_at: string
  clients: OpsClientStats
  emission: OpsEmissionStats
  timeline: OpsDayEmission[]
  delivery: OpsDeliveryStats
  health: OpsSystemHealth
}

export const opsApi = {
  stats: () => api.get<OpsSnapshot>('/api/v1/admin/ops/stats').then((r) => r.data),
}

// ─── PQRSF (canal de servicio SCC) ─────────────────────────────────
//
// Requisito num. 10 del art. 1.5.1.8.1.1 de la Res. DIAN 000227 de 2025:
// canal de Peticiones, Quejas, Reclamos, Sugerencias y Felicitaciones con
// trazabilidad y consulta, revisable por la DIAN. Los tickets son de SCC
// (no de un tax_client), por eso las rutas cuelgan de /admin/pqrsf y no
// de /admin/tax-clients/:client_id/…

export type PqrsfKind =
  | 'peticion'
  | 'queja'
  | 'reclamo'
  | 'sugerencia'
  | 'felicitacion'

export type PqrsfStatus = 'radicado' | 'en_tramite' | 'resuelto' | 'cerrado'

export type PqrsfEventType =
  | 'radicacion'
  | 'cambio_estado'
  | 'respuesta'
  | 'nota'
  | 'asignacion'

export type PqrsfActorKind = 'ciudadano' | 'admin' | 'sistema'

export const PQRSF_KIND_LABELS: Record<PqrsfKind, string> = {
  peticion: 'Petición',
  queja: 'Queja',
  reclamo: 'Reclamo',
  sugerencia: 'Sugerencia',
  felicitacion: 'Felicitación',
}

export const PQRSF_STATUS_LABELS: Record<PqrsfStatus, string> = {
  radicado: 'Radicado',
  en_tramite: 'En trámite',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
}

export const PQRSF_EVENT_LABELS: Record<PqrsfEventType, string> = {
  radicacion: 'Radicación',
  cambio_estado: 'Cambio de estado',
  respuesta: 'Respuesta',
  nota: 'Nota',
  asignacion: 'Asignación',
}

/** Transiciones permitidas — espejo de la máquina de estados del backend
 *  (services.IsValidPQRSFTransition). El backend sigue siendo la
 *  autoridad: esto sólo evita ofrecer una acción que va a devolver 409. */
export const PQRSF_ALLOWED_TRANSITIONS: Record<PqrsfStatus, PqrsfStatus[]> = {
  radicado: ['en_tramite', 'resuelto', 'cerrado'],
  en_tramite: ['resuelto', 'cerrado'],
  resuelto: ['cerrado', 'en_tramite'],
  cerrado: [],
}

export interface PqrsfTicket {
  id: string
  radicado: string
  radicado_year: number
  radicado_seq: number
  kind: PqrsfKind
  status: PqrsfStatus
  requester_name: string
  requester_doc_type?: string
  requester_doc_number: string
  requester_email: string
  requester_phone?: string
  subject: string
  description: string
  response_text?: string
  responded_at?: string | null
  responded_by_user_id?: string
  responded_by_email?: string
  filed_at: string
  due_at?: string | null
  first_response_at?: string | null
  closed_at?: string | null
  updated_at: string
  tax_client_id?: string
  source_channel: string
  source_ip?: string
  source_user_agent?: string
  privacy_accepted: boolean
}

export interface PqrsfEvent {
  id: string
  ticket_id: string
  seq: number
  event_type: PqrsfEventType
  from_status?: string
  to_status?: string
  actor_kind: PqrsfActorKind
  actor_user_id?: string
  actor_email?: string
  comment?: string
  is_public: boolean
  prev_hash?: string
  this_hash: string
  ip_address?: string
  user_agent?: string
  created_at: string
}

export interface PqrsfDetail {
  ticket: PqrsfTicket
  timeline: PqrsfEvent[]
  /** Resultado de recalcular la cadena de hashes de la trazabilidad. */
  chain_valid: boolean
}

export interface PqrsfListResponse {
  items: PqrsfTicket[]
  total: number
  limit: number
  offset: number
}

export interface PqrsfStats {
  by_status: Record<string, number>
  by_kind: Record<string, number>
  overdue: number
  total: number
}

export interface PqrsfFilter {
  status?: PqrsfStatus | ''
  kind?: PqrsfKind | ''
  search?: string
  overdue?: boolean
  limit?: number
  offset?: number
}

export const pqrsfApi = {
  list: (filter: PqrsfFilter) =>
    api
      .get<PqrsfListResponse>('/api/v1/admin/pqrsf/tickets', {
        params: {
          status: filter.status || undefined,
          kind: filter.kind || undefined,
          search: filter.search || undefined,
          overdue: filter.overdue ? 'true' : undefined,
          limit: filter.limit,
          offset: filter.offset,
        },
      })
      .then((r) => r.data),

  stats: () => api.get<PqrsfStats>('/api/v1/admin/pqrsf/stats').then((r) => r.data),

  get: (id: string) =>
    api.get<PqrsfDetail>(`/api/v1/admin/pqrsf/tickets/${id}`).then((r) => r.data),

  respond: (id: string, body: { response_text: string; status?: PqrsfStatus }) =>
    api
      .post<PqrsfTicket>(`/api/v1/admin/pqrsf/tickets/${id}/respond`, body)
      .then((r) => r.data),

  changeStatus: (id: string, body: { status: PqrsfStatus; comment?: string }) =>
    api
      .post<PqrsfTicket>(`/api/v1/admin/pqrsf/tickets/${id}/status`, body)
      .then((r) => r.data),

  addNote: (id: string, body: { comment: string; is_public: boolean }) =>
    api
      .post<PqrsfEvent>(`/api/v1/admin/pqrsf/tickets/${id}/notes`, body)
      .then((r) => r.data),
}
